import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import AuthModal from '@/components/AuthModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getTotal, clearCart } = useCart();
  const { user, token } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: ''
  });
  const [processing, setProcessing] = useState(false);

  const currency = cartItems[0]?.price === 999 ? 'INR' : 'USD';
  const currencySymbol = currency === 'INR' ? '₹' : '$';

  const handleCheckout = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    setShowCheckout(true);
  };

  const handlePlaceOrder = async () => {
    if (!shippingAddress.name || !shippingAddress.address || !shippingAddress.city || 
        !shippingAddress.state || !shippingAddress.pincode || !shippingAddress.phone) {
      toast.error('Please fill all shipping details');
      return;
    }

    setProcessing(true);
    try {
      // Create order
      const orderItems = cartItems.map(item => ({
        product_id: item.product_id,
        product_name: 'Cosmic Cat T-Shirt',
        size: item.size,
        color: item.color,
        quantity: item.quantity,
        price: item.price
      }));

      const orderResponse = await axios.post(
        `${API}/orders/create`,
        {
          items: orderItems,
          shipping_address: shippingAddress,
          currency: currency
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const orderId = orderResponse.data.order_id;

      // Create payment session
      const paymentResponse = await axios.post(
        `${API}/payments/create-session`,
        {
          order_id: orderId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe
      window.location.href = paymentResponse.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process checkout');
      setProcessing(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen relative">
        <div className="stars"></div>
        <Navbar />
        <div className="relative z-10 pt-32 pb-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="glass-card p-12" data-testid="empty-cart">
              <ShoppingBag className="w-24 h-24 mx-auto mb-6 text-gray-600" />
              <h2 className="text-3xl font-bold mb-4">Your Cart is Empty</h2>
              <p className="text-gray-400 mb-8">Start adding some cosmic t-shirts to your cart!</p>
              <button 
                onClick={() => navigate('/shop')}
                className="btn-primary"
                data-testid="shop-now-empty-cart"
              >
                Start Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="stars"></div>
      <Navbar />

      <div className="relative z-10 pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-8" data-testid="cart-title">Shopping Cart</h1>

          {!showCheckout ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {cartItems.map((item, index) => (
                  <div key={index} className="glass-card" data-testid={`cart-item-${index}`}>
                    <div className="flex gap-4">
                      <img 
                        src="/tshirt_black_0.png" 
                        alt="Product"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">Cosmic Cat T-Shirt</h3>
                        <div className="flex gap-4 text-sm text-gray-400 mb-3">
                          <span>Size: <span className="text-white">{item.size}</span></span>
                          <span>Color: <span className="text-white">{item.color}</span></span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item, item.quantity - 1)}
                              className="w-8 h-8 rounded border border-gray-700 hover:border-gray-500 flex items-center justify-center"
                              data-testid={`decrease-qty-${index}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-12 text-center font-semibold" data-testid={`qty-${index}`}>{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item, item.quantity + 1)}
                              className="w-8 h-8 rounded border border-gray-700 hover:border-gray-500 flex items-center justify-center"
                              data-testid={`increase-qty-${index}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xl font-bold">{currencySymbol}{(item.price * item.quantity).toFixed(2)}</span>
                            <button
                              onClick={() => removeFromCart(item)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`remove-item-${index}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="glass-card sticky top-32" data-testid="order-summary">
                  <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Subtotal</span>
                      <span className="font-semibold">{currencySymbol}{getTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Shipping</span>
                      <span className="font-semibold">Free</span>
                    </div>
                    <div className="border-t border-gray-700 pt-3">
                      <div className="flex justify-between text-xl">
                        <span className="font-bold">Total</span>
                        <span className="font-bold">{currencySymbol}{getTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={handleCheckout}
                    className="w-full btn-primary py-4 text-lg"
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              <div className="glass-card" data-testid="checkout-form">
                <h2 className="text-2xl font-bold mb-6">Shipping Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={shippingAddress.name}
                      onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                      className="w-full"
                      placeholder="John Doe"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Address *</label>
                    <textarea
                      value={shippingAddress.address}
                      onChange={(e) => setShippingAddress({...shippingAddress, address: e.target.value})}
                      className="w-full"
                      rows="3"
                      placeholder="Street address, apartment, suite, etc."
                      data-testid="input-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">City *</label>
                      <input
                        type="text"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                        className="w-full"
                        placeholder="Mumbai"
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">State *</label>
                      <input
                        type="text"
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                        className="w-full"
                        placeholder="Maharashtra"
                        data-testid="input-state"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Pincode *</label>
                      <input
                        type="text"
                        value={shippingAddress.pincode}
                        onChange={(e) => setShippingAddress({...shippingAddress, pincode: e.target.value})}
                        className="w-full"
                        placeholder="400001"
                        data-testid="input-pincode"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Phone *</label>
                      <input
                        type="tel"
                        value={shippingAddress.phone}
                        onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                        className="w-full"
                        placeholder="+91 9876543210"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-700">
                  <div className="flex justify-between text-xl font-bold mb-6">
                    <span>Total Amount</span>
                    <span data-testid="total-amount">{currencySymbol}{getTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setShowCheckout(false)}
                      className="flex-1 btn-secondary"
                      disabled={processing}
                      data-testid="back-to-cart-btn"
                    >
                      Back to Cart
                    </button>
                    <button
                      onClick={handlePlaceOrder}
                      className="flex-1 btn-primary"
                      disabled={processing}
                      data-testid="place-order-btn"
                    >
                      {processing ? 'Processing...' : 'Place Order'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default Cart;
